import { useEffect, useRef, useState } from "react";
import api, { fileUrl } from "@/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Printer, FileDown, Loader2, FileText } from "lucide-react";

/**
 * DischargeSummaryModal — one-click professional discharge summary PDF.
 *
 * Fetches `/api/ipd/admissions/{id}/discharge-summary` which returns a
 * fully-hydrated bundle (hospital header, patient, doctor, admission,
 * daily-note medications & procedures, final bill). Renders a clean
 * printable HTML template in the modal AND provides a "Print / Save as
 * PDF" button that opens the same DOM in a popup window (same pattern
 * as BillPreviewModal — proven cross-browser).
 *
 * The template intentionally uses inline styles + Georgia serif so the
 * popup renders identically without Tailwind.
 */
export default function DischargeSummaryModal({ open, onClose, admissionId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const printRef = useRef(null);

  useEffect(() => {
    if (!open || !admissionId) return undefined;
    setLoading(true); setError(null); setData(null);
    api.get(`/ipd/admissions/${admissionId}/discharge-summary`)
      .then(({ data }) => setData(data))
      .catch((e) => setError(e?.response?.data?.detail || "Failed to load"))
      .finally(() => setLoading(false));
    return undefined;
  }, [open, admissionId]);

  const money = (n) => `₹${Number(n || 0).toLocaleString("en-IN")}`;
  const fmt = (d) => d ? new Date(d).toLocaleDateString("en-IN",
    { day: "numeric", month: "short", year: "numeric" }) : "—";

  const handlePrint = () => {
    if (!printRef.current) return;
    const w = window.open("", "_blank", "width=900,height=1000");
    if (!w) { toast.error("Pop-up blocked — allow pop-ups to print"); return; }
    w.document.write(`<!DOCTYPE html>
<html>
<head>
  <meta charset="UTF-8">
  <title>Discharge Summary · ${(data?.patient?.name || "").slice(0, 30)}</title>
  <style>
    * { margin:0; padding:0; box-sizing:border-box;
        -webkit-print-color-adjust: exact; print-color-adjust: exact; }
    body { font-family: Georgia, "Times New Roman", serif; color:#111;
           font-size: 12px; line-height: 1.55; background:#fff; padding: 24px; }
    h1 { font-size: 20px; margin-bottom:2px; }
    h2 { font-size: 13px; text-transform: uppercase; letter-spacing: 1.5px;
         color:#334155; border-bottom:1px solid #cbd5e1; padding-bottom:4px;
         margin: 14px 0 6px; }
    p { margin: 2px 0; }
    table { width:100%; border-collapse: collapse; margin-top:6px; font-size:11px; }
    th, td { border:1px solid #334155; padding:5px 7px; text-align:left; }
    th { background:#f1f5f9; font-weight:bold; }
    .hdr { display:flex; justify-content:space-between; align-items:center;
           border-bottom:3px double #0f172a; padding-bottom:8px; margin-bottom:12px; }
    .hdr img { max-height:60px; max-width:80px; object-fit:contain; }
    .hdr .txt { flex:1; text-align:center; }
    .hdr .stamp { text-align:right; font-size:11px; color:#475569; }
    .grid2 { display:grid; grid-template-columns: 1fr 1fr; gap:8px 20px; }
    .grid3 { display:grid; grid-template-columns: repeat(3,1fr); gap:6px 16px; }
    .label { font-size:10px; text-transform:uppercase; color:#64748b;
             letter-spacing:1px; margin-bottom:2px; }
    ul { padding-left:18px; }
    li { margin: 2px 0; }
    .sig { display:flex; justify-content:space-between; margin-top:32px; }
    .sig div { text-align:center; width: 40%; }
    .sig .line { border-top:1px solid #334155; margin-top:36px;
                 padding-top:4px; font-size:11px; }
    .stamp-badge { display:inline-block; padding:2px 8px; border-radius:12px;
                   font-size:10px; font-weight:bold; }
    .paid { background:#d1fae5; color:#065f46; }
    .due  { background:#fee2e2; color:#991b1b; }
    @media print { body { padding: 0; } }
  </style>
</head>
<body>${printRef.current.innerHTML}</body>
</html>`);
    w.document.close();
    setTimeout(() => { w.focus(); w.print(); }, 300);
  };

  const bill = data?.billing;
  const paid = bill?.payment_status === "paid";
  const rawLogo = data?.hospital?.logo_url || "";
  const logoSrc = /^(https?:|data:)/i.test(rawLogo) ? rawLogo : fileUrl(rawLogo);

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden flex flex-col"
                     data-testid="ipd-discharge-summary-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileText size={18} className="text-blue-600" />
            Discharge Summary
          </DialogTitle>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto -mx-6 px-6 py-2">
          {loading ? (
            <div className="text-center py-10 text-slate-500">
              <Loader2 className="mx-auto animate-spin mb-2" size={22} />
              Assembling discharge summary…
            </div>
          ) : error ? (
            <div className="text-center py-10 text-rose-600">{error}</div>
          ) : data ? (
            <div ref={printRef} className="bg-white text-slate-900 text-sm
                                            [&_.label]:block [&_.label]:text-[10px]
                                            [&_.label]:uppercase [&_.label]:tracking-widest
                                            [&_.label]:text-slate-500 [&_.label]:mb-0.5
                                            [&_.hdr]:flex [&_.hdr]:justify-between [&_.hdr]:items-center
                                            [&_.hdr]:border-b-2 [&_.hdr]:border-slate-800 [&_.hdr]:pb-3 [&_.hdr]:mb-4
                                            [&_.hdr_img]:max-h-14 [&_.hdr_img]:max-w-16 [&_.hdr_img]:object-contain
                                            [&_.hdr_.txt]:flex-1 [&_.hdr_.txt]:text-center
                                            [&_.hdr_.stamp]:text-right [&_.hdr_.stamp]:text-xs [&_.hdr_.stamp]:text-slate-500
                                            [&_h2]:text-xs [&_h2]:uppercase [&_h2]:tracking-widest
                                            [&_h2]:text-slate-700 [&_h2]:border-b [&_h2]:border-slate-200
                                            [&_h2]:pb-1 [&_h2]:mt-4 [&_h2]:mb-2
                                            [&_.grid2]:grid [&_.grid2]:grid-cols-2 [&_.grid2]:gap-x-6 [&_.grid2]:gap-y-2
                                            [&_.grid3]:grid [&_.grid3]:grid-cols-3 [&_.grid3]:gap-x-6 [&_.grid3]:gap-y-2
                                            [&_table]:w-full [&_table]:mt-2 [&_table]:border-collapse
                                            [&_th]:border [&_th]:border-slate-400 [&_th]:bg-slate-100 [&_th]:px-2 [&_th]:py-1 [&_th]:text-xs [&_th]:text-left
                                            [&_td]:border [&_td]:border-slate-400 [&_td]:px-2 [&_td]:py-1 [&_td]:text-xs
                                            [&_ul]:pl-5 [&_ul]:list-disc [&_li]:text-xs
                                            [&_.sig]:flex [&_.sig]:justify-between [&_.sig]:mt-8
                                            [&_.sig_div]:text-center [&_.sig_.line]:border-t [&_.sig_.line]:border-slate-500
                                            [&_.sig_.line]:mt-8 [&_.sig_.line]:pt-1 [&_.sig_.line]:text-xs
                                            [&_.stamp-badge]:inline-block [&_.stamp-badge]:px-2 [&_.stamp-badge]:py-0.5
                                            [&_.stamp-badge]:rounded-full [&_.stamp-badge]:text-[10px] [&_.stamp-badge]:font-bold
                                            [&_.paid]:bg-emerald-100 [&_.paid]:text-emerald-800
                                            [&_.due]:bg-rose-100 [&_.due]:text-rose-800">
              {/* Header */}
              <div className="hdr">
                {logoSrc ? (
                  <img src={logoSrc} alt="Logo" />
                ) : <div style={{ width: 60 }} />}
                <div className="txt">
                  <h1>{data.hospital.name || "Hospital"}</h1>
                  <p style={{ fontSize: 11, color: "#475569" }}>
                    {[data.hospital.address, data.hospital.city].filter(Boolean).join(", ")}
                  </p>
                  <p style={{ fontSize: 11, color: "#475569" }}>
                    {data.hospital.phone} {data.hospital.email && " · " + data.hospital.email}
                  </p>
                  <p style={{ fontSize: 15, marginTop: 6, letterSpacing: 3, fontWeight: "bold" }}>
                    DISCHARGE SUMMARY
                  </p>
                </div>
                <div className="stamp">
                  {data.hospital.registration_no && (
                    <p>Reg No: {data.hospital.registration_no}</p>
                  )}
                  <p>ID: {(data.admission.id || "").slice(0, 8).toUpperCase()}</p>
                  <p>{fmt(data.generated_at)}</p>
                </div>
              </div>

              {/* Patient + Doctor */}
              <h2>Patient &amp; Doctor</h2>
              <div className="grid3">
                <div><span className="label">Patient</span>{data.patient.name}</div>
                <div><span className="label">Age / Gender</span>
                  {data.patient.age ?? "—"} / {data.patient.gender || "—"}</div>
                <div><span className="label">Mobile</span>{data.patient.mobile || "—"}</div>
                <div><span className="label">Doctor</span>
                  Dr. {data.doctor.name}{data.doctor.verified ? "  ✔" : ""}</div>
                <div><span className="label">Specialization</span>
                  {data.doctor.specialization || "—"}</div>
                <div><span className="label">NMC</span>{data.doctor.nmc_number || "—"}</div>
              </div>

              {/* Admission */}
              <h2>Admission</h2>
              <div className="grid3">
                <div><span className="label">Admitted</span>{fmt(data.admission.admission_date)}</div>
                <div><span className="label">Discharged</span>{fmt(data.admission.discharge_date)}</div>
                <div><span className="label">Stay</span>{data.admission.length_of_stay} day(s)</div>
                <div><span className="label">Ward</span>{data.admission.ward_name || "—"}</div>
                <div><span className="label">Bed</span>{data.admission.bed_number || "—"}</div>
                <div><span className="label">Status</span>{data.admission.status}</div>
              </div>

              {/* Clinical */}
              <h2>Clinical Details</h2>
              <div className="grid2">
                <div>
                  <span className="label">Chief complaint</span>
                  {data.admission.chief_complaint || "—"}
                </div>
                <div>
                  <span className="label">Diagnosis</span>
                  {data.summary?.discharge_diagnosis || data.admission.reason_for_admission || "—"}
                </div>
                <div>
                  <span className="label">Allergies</span>
                  {data.admission.allergies || "None reported"}
                </div>
                <div>
                  <span className="label">Comorbidities</span>
                  {data.admission.comorbidities || "None reported"}
                </div>
              </div>

              {/* Medications */}
              {(data.medications?.length > 0 || data.summary?.discharge_medications?.length > 0) && (
                <>
                  <h2>Medications</h2>
                  {data.medications?.length > 0 ? (
                    <table>
                      <thead>
                        <tr>
                          <th>Medicine</th><th>Dose</th><th>Frequency</th><th>Duration</th>
                        </tr>
                      </thead>
                      <tbody>
                        {data.medications.map((m, i) => (
                          <tr key={i}>
                            <td>{m.name}</td>
                            <td>{m.dose || "—"}</td>
                            <td>{m.frequency || "—"}</td>
                            <td>{m.duration ? `${m.duration} day(s)` : "—"}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  ) : (
                    <ul>{data.summary.discharge_medications.map((m, i) =>
                      <li key={i}>{m}</li>)}</ul>
                  )}
                </>
              )}

              {/* Procedures */}
              {(data.summary?.procedures_done?.length > 0
                  || data.procedures_from_notes?.length > 0) && (
                <>
                  <h2>Procedures</h2>
                  <ul>
                    {(data.summary?.procedures_done || []).map((p, i) =>
                      <li key={`sp-${i}`}>{p}</li>)}
                    {(data.procedures_from_notes || []).map((p, i) =>
                      <li key={`np-${i}`}>{p}</li>)}
                  </ul>
                </>
              )}

              {/* Follow-up + restrictions */}
              <h2>Follow-up &amp; Restrictions</h2>
              <div className="grid2">
                <div>
                  <span className="label">Follow-up on</span>
                  {fmt(data.summary?.follow_up_date)}
                  {data.summary?.follow_up_doctor && ` with ${data.summary.follow_up_doctor}`}
                </div>
                <div>
                  <span className="label">Instructions</span>
                  {data.summary?.follow_up_instructions || "Return in 7 days for review"}
                </div>
                <div>
                  <span className="label">Dietary restrictions</span>
                  {data.summary?.dietary_restrictions || "As advised by the treating doctor"}
                </div>
                <div>
                  <span className="label">Activity restrictions</span>
                  {data.summary?.activity_restrictions || "Avoid strenuous activity for 7 days"}
                </div>
              </div>

              {/* Bill */}
              {bill && (
                <>
                  <h2>Billing Summary</h2>
                  <div className="grid3">
                    <div><span className="label">Bill No</span>{bill.bill_no || bill.id?.slice(0, 8)}</div>
                    <div><span className="label">Total</span>{money(bill.total_amount)}</div>
                    <div><span className="label">Paid</span>{money(bill.amount_paid)}</div>
                    <div><span className="label">Balance</span>{money(bill.amount_balance)}</div>
                    <div>
                      <span className="label">Status</span>
                      <span className={`stamp-badge ${paid ? "paid" : "due"}`}>
                        {(bill.payment_status || "pending").toUpperCase()}
                      </span>
                    </div>
                  </div>
                </>
              )}

              {/* Signatures */}
              <div className="sig">
                <div><div className="line">Doctor Signature</div>
                  <p style={{ fontSize: 10, color: "#64748b" }}>Dr. {data.doctor.name}</p>
                </div>
                <div><div className="line">Hospital Stamp</div>
                  <p style={{ fontSize: 10, color: "#64748b" }}>{data.hospital.name}</p>
                </div>
              </div>
            </div>
          ) : null}
        </div>

        <DialogFooter className="border-t pt-3">
          <Button variant="outline" onClick={onClose}>Close</Button>
          <Button onClick={handlePrint} disabled={!data}
                  className="bg-blue-600 hover:bg-blue-700 text-white"
                  data-testid="ipd-discharge-summary-print">
            <Printer size={14} className="mr-1" /> Print / Save as PDF
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
